import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CardActionGroup } from '@/common/components/CardActionGroup/CardActionGroup';
import { CardActionUpload } from '@/common/components/CardActionUpload/CardActionUpload';
import { ControlledDateField } from '@/common/components/Fields/ControlledDateField/ControlledDateField';
import { ControlledTextField } from '@/common/components/Fields/ControlledTextField/ControlledTextField';
import { Form } from '@/common/components/Form/Form';
import { Icon } from '@/common/components/Icons/Icon';
import { Stack } from '@/common/components/Stack/Stack';
import { Translate } from '@/common/components/Translate/Translate';
import { useAddOrUpdateEmployeeIdentification } from '@/common/integrations/menhir/employee/employee/employee.hooks';
import { FlowLayout } from '@/common/layouts/FlowLayout/FlowLayout';
import { useFlowLayout } from '@/common/layouts/FlowLayout/FlowLayout.context';
import { formatDateISO } from '@/common/utils/date';
import { notify } from '@/common/utils/toast/toast';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  TTransportationCardFormValues,
  transportationCardFormSchema,
} from './TransporationCardForm.schema';

export function TransportationCardForm() {
  const { t } = useTranslation();
  const { dismiss } = useFlowLayout();
  const { mutateAsync } =
    useAddOrUpdateEmployeeIdentification<'transportation-card'>();

  const methods = useForm<TTransportationCardFormValues>({
    mode: 'onTouched',
    defaultValues: {
      number: '',
      expiryDate: undefined,
      transportationCardFilesFront: null,
      transportationCardFilesBack: null,
    },
    resolver: zodResolver(transportationCardFormSchema),
  });

  const onSubmit = async ({
    number,
    expiryDate,
    transportationCardFilesFront,
    transportationCardFilesBack,
  }: TTransportationCardFormValues) => {
    try {
      if (!transportationCardFilesFront || !transportationCardFilesBack) {
        throw new Error(t('errors.input.required'));
      }
      const newIdentification = await mutateAsync({
        documentType: 'transportation-card',
        details: {
          number,
          expires_at: formatDateISO(expiryDate),
        },
        files: [transportationCardFilesFront, transportationCardFilesBack],
      });

      if (newIdentification) {
        notify({
          type: 'success',
          message: t('dashboard.document_edit.transportation_card.success'),
        });
        dismiss();
      }
    } catch {
      notify({
        type: 'error',
        message: t('errors.api.server'),
      });
    }
  };

  const [transportationCardFilesFront, transportationCardFilesBack] =
    methods.watch([
      'transportationCardFilesFront',
      'transportationCardFilesBack',
    ]);

  return (
    <FlowLayout.Content
      primaryAction={{
        children: t('general.navigation.save'),
        onPress: methods.handleSubmit(onSubmit),
        loading: methods.formState.isSubmitting,
        disabled: !methods.formState.isValid,
      }}
    >
      <FormProvider {...methods}>
        <Form onSubmit={methods.handleSubmit(onSubmit)}>
          <Stack gap="form">
            <ControlledTextField
              name="number"
              label={t('transportation_card_form.fields.number.label')}
            />
            <ControlledDateField
              name="expiryDate"
              label={t('transportation_card_form.fields.expiry_date.label')}
            />
            <CardActionGroup
              title={t('transportation_card_form.fields.files.label')}
              description={t(
                'transportation_card_form.fields.files.description'
              )}
            >
              <CardActionUpload
                title={
                  <Translate i18nKey="transportation_card_form.fields.files.title.front" />
                }
                icon={<Icon icon="id-card" />}
                file={transportationCardFilesFront ?? undefined}
                onChange={(file) => {
                  if (!file) {
                    methods.setValue('transportationCardFilesFront', null, {
                      shouldValidate: true,
                    });
                    return;
                  }
                  methods.setValue('transportationCardFilesFront', file, {
                    shouldValidate: true,
                  });
                }}
              />
              <CardActionUpload
                title={
                  <Translate i18nKey="transportation_card_form.fields.files.title.back" />
                }
                icon={<Icon icon="id-card" />}
                file={transportationCardFilesBack ?? undefined}
                onChange={(file) => {
                  if (!file) {
                    methods.setValue('transportationCardFilesBack', null, {
                      shouldValidate: true,
                    });
                    return;
                  }
                  methods.setValue('transportationCardFilesBack', file, {
                    shouldValidate: true,
                  });
                }}
              />
            </CardActionGroup>
          </Stack>
        </Form>
      </FormProvider>
    </FlowLayout.Content>
  );
}
